import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { API_URL } from "../lib/supabase";
import { CATEGORY_HERO_IMAGES, getServiceGradient } from "../lib/constants";
import { ServiceIcon } from "../components/shared/ServiceIcon";
import { MessageSquare, ChevronRight, ArrowRight } from "lucide-react";

const CategoryDetailPage = () => {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategoryData();
  }, [slug]);

  const fetchCategoryData = async () => {
    setLoading(true);
    try {
      const [catRes, servicesRes] = await Promise.all([
        fetch(`${API_URL}/categories/${slug}`),
        fetch(`${API_URL}/services?category_slug=${slug}`)
      ]);
      
      const catData = await catRes.json();
      const servicesData = await servicesRes.json();
      
      setCategory(catData);
      setServices(servicesData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!category) return <div className="error">Category not found</div>;

  const heroImage = CATEGORY_HERO_IMAGES[slug] || CATEGORY_HERO_IMAGES['handyman'];
  const fallbackImage = 'https://images.unsplash.com/photo-1581783898377-1c85bf937427?w=1200&q=80';
  const imagePosition = {
    'flooring': 'center bottom',
    'fencing-gates': 'center bottom',
  }[slug] || 'center';

  return (
    <div className="page category-detail-page">
      {/* Category Hero Section */}
      <div className="category-hero" data-testid="category-hero">
        <img 
          src={heroImage} 
          alt={category.name}
          className="category-hero-img"
          style={{ objectPosition: imagePosition }}
          onError={(e) => { e.target.onerror = null; e.target.src = fallbackImage; }}
        />
        <div className="category-hero-overlay" />
        <div className="category-hero-content">
          <div className="breadcrumb breadcrumb-hero">
            <Link to="/categories">Categories</Link>
            <ChevronRight size={16} />
            <span>{category.name}</span>
          </div>
          <h1 data-testid="category-hero-title">{category.name}</h1>
          <p>Browse popular services near you, or request a quote for any job not listed below.</p>
        </div>
      </div>

      <div className="services-grid" data-testid="category-services">
        {services.map((service) => (
          <div key={service.id} className="service-card" data-testid={`service-${service.slug}`}>
            <Link to={`/services/${service.slug}`} className="service-card-link">
              <div className={`svc-card-header ${getServiceGradient(service)}`}>
                <div className="svc-icon-glow">
                  <ServiceIcon slug={service.slug} size={30} />
                </div>
              </div>
              <div className="service-content">
                <h3>{service.name}</h3>
                <p>{service.short_description}</p>
                <div className="service-price">{service.price_display}</div>
              </div>
            </Link>
            <div className="service-card-cta">
              <Link to={`/quote-request/${service.slug}`} className="btn-quote" data-testid={`quote-${service.slug}`}>
                Request a Quote <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        ))}

        {/* Fallback "Other Service" card */}
        <div className="service-card other-service-card" data-testid="other-service-card">
          <div className="other-service-content">
            <div className="other-service-icon">
              <MessageSquare size={28} />
            </div>
            <h3>Need another service?</h3>
            <p>Can't find your service? Request a quote for any {category.name.toLowerCase()} job.</p>
          </div>
          <div className="service-card-cta">
            <Link to={`/quote-request/other?category=${slug}`} className="btn-quote" data-testid="quote-other-service">
              Request a Quote <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>

      {services.length === 0 && (
        <div className="empty-state">
          <p>No services found in this category yet.</p>
        </div>
      )}
    </div>
  );
};


export default CategoryDetailPage;
